import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import fileDownload from 'js-file-download';
import { format } from 'date-fns';
import { withStyles } from '@mui/styles';
import { LoadingView, DynamicTable, CSVExportDialog } from 'components';
import { LocationTypeUtil } from 'doctivity-shared/utils';
import { GetApp } from '@mui/icons-material';
import axiosInstance from 'utils/axiosUtil';
import { Card, CardContent, Typography, IconButton } from '@mui/material';

// this controls which columns are displayed and how they are looked up in data
const columns = [
  {
    label: 'Code',
    key: 'code.code',
    style: { width: 80 },
  },
  {
    label: 'Scheme',
    key: 'code.code_type',
    style: { width: 80 },
    format: (value) => {
      if (value === 1) {
        return 'HCPCS';
      } else if (value === 2) {
        return 'ICD9';
      } else if (value === 3) {
        return 'ICD10-CM';
      } else if (value === 4) {
        return 'ICD10-PCS';
      }
      return '';
    },
  },
  {
    label: 'Claims',
    key: 'claim_count',
    style: { width: 100 },
    format: (value) => (value ? value : '< 11'),
  },
  {
    label: 'Reimbursement',
    key: 'code.medicare_fees',
    style: { width: 150 },
    format: (value, row) => {
      // Organizations: the backend computes a facility-correct value (OPPS/ASC/CLFS)
      // and attaches it per row. Providers: compute the PFS value client-side below.
      if (row.reimbursement_value != null) {
        return `$${Math.round(row.reimbursement_value).toLocaleString()}`;
      }
      if (value?.[0]?.non_facility_fee) {
        let reimbursement = Number.parseFloat(value[0].non_facility_fee);
        if (row.claim_count) {
          reimbursement *= row.claim_count;
        } else {
          reimbursement *= 5;
        }
        return `$${Math.round(reimbursement).toLocaleString()}`;
      }
      return '';
    },
    filter: false,
    sortable: false,
  },
  {
    label: 'Percent',
    key: 'claim_percent',
    style: { width: 100 },
    format: (value) => `${value}%`,
  },
  {
    label: 'Setting',
    key: 'location_type',
    style: { width: 100 },
    format: (value) => (value ? LocationTypeUtil.getName(value) : ''),
    filter: false,
    sortable: false,
  },
  {
    label: 'Description',
    key: 'code.description',
  },
];

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  card: {},
  pdfButton: {
    marginLeft: theme.spacing(),
    flex: 1,
  },
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  pdfIcon: {
    fontSize: 16,
    fontWeight: 200,
  },
});

class CodesTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      query: {
        where: {},
        include: [
          {
            association: 'code',
            required: true,
          },
        ],
        order: [['claim_count', 'DESC']],
        limit: 25,
        offset: 0,
      },
      data: null,
      loading: true,
      localityData: null,
      csvDialogOpen: false,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const providerChanged = this.props.provider?.id !== prevProps.provider?.id;
    const orgChanged = this.props.organization?.id !== prevProps.organization?.id;
    
    if (providerChanged || orgChanged) {
      this.setState({ data: null, loading: true }, () => {
        this.loadData();
      });
    }
  }

  async loadData() {
    try {
      // For PX codes, fetch locality first
      if (!this.props.diagnosis && this.props.provider?.location?.postal_code) {
        const postalCode = this.props.provider.location.postal_code;
        
        // Fetch locality data
        const localityResponse = await axiosInstance.get('/MedicarePostalToLocality', {
          params: { opts: { where: { postal_code: postalCode } } }
        });
        
        const locality = localityResponse.data?.[0];
        this.setState({ localityData: locality });
        
        // Now fetch codes with locality
        await this.fetchData(locality);
      } else {
        // DX codes - fetch immediately
        await this.fetchData();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      this.setState({ loading: false });
    }
  }

  async fetchData(localityData = null, queryOverride = null) {
    const query = queryOverride || this.state.query; 
    const queryToSend = JSON.parse(JSON.stringify(query)); 
    
    // Add locality for PX codes
    if (localityData?.locality && localityData?.carrier) {
      queryToSend.include[0].include = [
        {
          attributes: ['non_facility_fee', 'facility_fee'],
          association: 'medicare_fees',
          required: false,
          duplicating: false,
          where: {
            locality: localityData.locality,
            carrier: localityData.carrier,
          },
        },
      ];
    }
    
    // Add provider/org filter
    if (!queryToSend.where) {
      queryToSend.where = {};
    }
    if (this.props.provider) {
      queryToSend.where.provider_id = this.props.provider.id;
    } else if (this.props.organization) {
      queryToSend.where.organization_id = this.props.organization.id;
    }
    
    try {
      const response = await axiosInstance.get(this.getEndpoint(), {
        params: { opts: queryToSend }
      });
      
      // Only update loading if it was set to true
      this.setState({ 
        data: response.data,
        ...(this.state.loading && { loading: false })
      });
    } catch (error) {
      console.error('Failed to fetch codes:', error);
      if (this.state.loading) {
        this.setState({ loading: false });
      }
    }
  }

  getId() {
    if (this.props.provider) {
      return this.props.provider.id;
    } else if (this.props.organization) {
      return this.props.organization.id;
    }
    return null;
  }

  getTableName() {
    return `${this.props.provider ? 'Provider' : 'Organization'}${
      this.props.diagnosis ? 'Dx' : 'Px'
    }CodeCounts`;
  }

  // Org procedure codes use a v2 endpoint that returns the same rows plus a computed
  // facility `reimbursement_value` (OPPS/ASC/CLFS). Everything else (providers, all Dx)
  // uses the generic CRUD endpoint.
  getEndpoint() {
    if (this.props.organization && !this.props.diagnosis) {
      return `/v2/captured-codes/organizations/${this.props.organization.id}/px-codes`;
    }
    return `/${this.getTableName()}`;
  }

  getFilename() {
    const { provider, organization, diagnosis } = this.props;

    const name = provider
      ? `${provider.last_name}_${provider.first_name}`
      : organization
      ? organization.name
      : '';

    return `doctivity_${name}_captured_${
      diagnosis ? 'dx' : 'px'
    }_codes_${format(new Date(), 'yy_MM_dd')}.csv`;
  }

  fetchCSVDownload({ limit, offset, fileName } = {}) {
    const { query, localityData } = this.state;
    const queryToSend = JSON.parse(JSON.stringify(query));

    // Row count / start row come from the CSV export dialog; fall back to the on-screen
    // query when not provided. The endpoint already honors opts.limit/offset.
    if (typeof limit === 'number') {
      queryToSend.limit = limit;
      queryToSend.offset = typeof offset === 'number' ? offset : 0;
    }

    // Add locality include for PX codes (same as fetchData)
    if (localityData?.locality && localityData?.carrier) {
      queryToSend.include[0].include = [
        {
          attributes: ['non_facility_fee', 'facility_fee'],
          association: 'medicare_fees',
          required: false,
          duplicating: false,
          where: {
            locality: localityData.locality,
            carrier: localityData.carrier,
          },
        },
      ];
    }

    // Add provider/org filter
    if (!queryToSend.where) {
      queryToSend.where = {};
    }
    if (this.props.provider) {
      queryToSend.where.provider_id = this.props.provider.id;
    } else if (this.props.organization) {
      queryToSend.where.organization_id = this.props.organization.id;
    }

    axiosInstance
      .get(this.getEndpoint(), {
        timeout: 300000,
        params: { opts: queryToSend },
      })
      .then((response) => {
        const rows = response.data?.rows ?? response.data ?? [];
        const csv = this.buildCSV(Array.isArray(rows) ? rows : []);
        fileDownload(csv, fileName || this.getFilename());
      })
      .catch((error) => {
        console.error('CSV download failed:', error);
      });
  }

  buildCSV(rows) {
    const codeTypeLabel = (value) => {
      if (value === 1) return 'HCPCS';
      if (value === 2) return 'ICD9';
      if (value === 3) return 'ICD10-CM';
      if (value === 4) return 'ICD10-PCS';
      return '';
    };

    const csvColumns = [
      { header: 'Code',        value: (r) => r.code?.code ?? '' },
      { header: 'Scheme',      value: (r) => codeTypeLabel(r.code?.code_type) },
      { header: 'Claims',        value: (r) => r.claim_count ?? '' },
      ...(!this.props.diagnosis ? [{ header: 'Reimbursement', value: (r) => {
        if (r.reimbursement_value != null) {
          return `$${Math.round(r.reimbursement_value).toLocaleString()}`;
        }
        const fee = r.code?.medicare_fees?.[0]?.non_facility_fee;
        if (!fee) return '';
        const reimbursement = Number.parseFloat(fee) * (r.claim_count || 5);
        return `$${Math.round(reimbursement).toLocaleString()}`;
      }}] : []),
      { header: 'Percent',     value: (r) => (r.claim_percent != null ? `${r.claim_percent}%` : '') },
      { header: 'Setting',     value: (r) => (r.location_type ? LocationTypeUtil.getName(r.location_type) : '') },
      { header: 'Description', value: (r) => r.code?.description ?? '' },
    ];

    const escape = (val) => {
      const str = String(val ?? '');
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const header = csvColumns.map((c) => c.header).join(',');
    const dataRows = rows.map((row) =>
      csvColumns.map((c) => escape(c.value(row))).join(',')
    );

    return [header, ...dataRows].join('\n');
  }

  render() {
    const { classes } = this.props;
    const { data, loading } = this.state;

    if (loading || !data) {
      return <LoadingView />;
    }

    // Reimbursement shows for procedure (Px) codes: providers with a postal code
    // (PFS, computed client-side) and organizations (facility value, from the backend).
    // Never for diagnosis (Dx) codes.
    const showReimbursement =
      !this.props.diagnosis &&
      (!!this.props.organization || !!this.props.provider?.location?.postal_code);
    let tableColumns = columns;
    if (!showReimbursement) {
      tableColumns = columns.filter((column) => column.key !== 'code.medicare_fees');
    }

    return (
      <Card>
        <CardContent>
          <div className={classes.tableHeader}>
            <Typography component='h2' variant='h6' color='primary'>
              {this.props.diagnosis
                ? 'Captured Diagnosis Codes'
                : 'Captured Procedure Codes'}
            </Typography>
            <div className={classes.pdfButton}>
              <IconButton
                onClick={() => this.setState({ csvDialogOpen: true })}
                size='large'
              >
                <GetApp className={classes.pdfIcon} />
              </IconButton>
            </div>
          </div>
          <CSVExportDialog
            open={this.state.csvDialogOpen}
            onClose={() => this.setState({ csvDialogOpen: false })}
            title={this.props.diagnosis ? 'Download Diagnosis Codes' : 'Download Procedure Codes'}
            exportType='Codes'
            totalCount={data?.count}
            defaultFileName={this.getFilename()}
            onConfirm={({ limit, offset, fileName }) => {
              this.setState({ csvDialogOpen: false });
              this.fetchCSVDownload({ limit, offset, fileName });
            }}
          />
          <DynamicTable
            idKey={['code_id','location_type']}
            noDataMessage='No codes captured.'
            ContainerType={React.Fragment}
            columns={tableColumns}
            query={this.state.query}
            data={data}
            onQueryChange={this.onQueryChange}
            onRowClick={this.onRowClick}
          />
        </CardContent>
      </Card>
    );
  }

  onQueryChange = async (query) => {
    // Check if this is just a filter change (not pagination/sorting)
    const isFilterChange = query.where !== this.state.query.where;
    const isPaginationChange = query.offset !== this.state.query.offset || query.limit !== this.state.query.limit;
    
    // Only show loading spinner for pagination, not for filters
    if (isPaginationChange) {
      this.setState({ query, loading: true });
    } else {
      this.setState({ query });
    }
    
    await this.fetchData(this.state.localityData, query);
  };


}

CodesTable.propTypes = {
  classes: PropTypes.object.isRequired,
  provider: PropTypes.object,
  organization: PropTypes.object,
  diagnosis: PropTypes.bool,
  user: PropTypes.object,
};

function mapStateToProps(state) {
  const { user } = state;

  return {
    user
  };
}

const styled = withStyles(styles)(CodesTable);
const connected = connect(mapStateToProps)(styled);
export { connected as CodesTable };